import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Separator } from './ui/separator';
import { 
  UsersIcon, 
  TrendingUpIcon, 
  CalendarIcon, 
  ExternalLinkIcon,
  SparklesIcon,
  UserCheckIcon,
  QrCodeIcon,
  ShareIcon,
  WalletIcon,
  DownloadIcon,
  ClockIcon
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CommunityHub = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [interestMatches, setInterestMatches] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);

  // Updated events with new dates
  const upcomingEvents = [
    { name: 'Welcome Fair', date: 'Wednesday, October 1, 2025', time: '6:00 PM', isSoon: true },
    { name: 'Introductory Event', date: 'Thursday, October 2, 2025', time: '6:00 PM', isSoon: true },
    { name: 'Quant Research Event', date: 'Thursday, October 9, 2025', time: '7:00 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 1', date: 'Monday, October 13, 2025', time: '6:00 PM', isSoon: false },
    { name: 'Workshop', date: 'Thursday, October 16, 2025', time: '6:30 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 2', date: 'Monday, October 20, 2025', time: '6:00 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 3', date: 'Monday, October 27, 2025', time: '6:00 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 4', date: 'Monday, November 3, 2025', time: '6:00 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 5', date: 'Monday, November 10, 2025', time: '6:00 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 6', date: 'Monday, November 17, 2025', time: '6:00 PM', isSoon: false },
    { name: 'Algothon', date: 'Saturday, November 22, 2025', time: '9:00 AM', isSoon: false },
    { name: 'AlgoCourse Lecture 7', date: 'Monday, November 24, 2025', time: '6:00 PM', isSoon: false },
    { name: 'AlgoCourse Lecture 8', date: 'Monday, December 1, 2025', time: '6:00 PM', isSoon: false },
  ];

  // Updated committee members
  const committeeMembers = [
    { name: 'Adam Snelling', role: 'President, AlgoSoc' },
    { name: 'Tvishi Agarwal', role: 'President, WiT' },
    { name: 'Utkarsh Jetly', role: 'Secretary, AlgoSoc' },
    { name: 'Anita Pergel', role: 'VP, WiT' },
    { name: 'Ilya Schor', role: 'Head of Events, AlgoSoc' },
    { name: 'Oles Chaban', role: 'Co-Head of Trading, AlgoSoc' },
    { name: 'Xitian Zhao', role: 'Treasurer, AlgoSoc' },
    { name: 'Conrad Trey Indradjaja', role: 'Co-Head of Trading, AlgoSoc' },
    { name: 'Moyin Olojede', role: 'Co-Event Manager, WiT' },
    { name: 'Olivia Brenninkmeijer', role: 'Co-Sponsorship Manager, WiT' },
    { name: 'AlQavi Hasan', role: 'Head of Back End, AlgoSoc' },
    { name: 'Kavya Sathish', role: 'Social Media Manager, WiT' },
    { name: 'Yana Aggarwal', role: 'Co-Event Manager, WiT' },
    { name: 'Isaac Chu', role: 'Head of Front End, AlgoSoc' },
    { name: 'Siya Gupta', role: 'Co-Sponsorship Manager, WiT' },
    { name: 'Khuselee Ganbold', role: 'Head of Marketing, AlgoSoc' },
  ];

  useEffect(() => {
    fetchCommunityData();
  }, [userId]);

  useEffect(() => {
    if (userProfile) {
      generateQRCode();
    }
  }, [userProfile]);

  const fetchCommunityData = async () => {
    try {
      setLoading(true);

      // Fetch user profile
      const userResponse = await axios.get(`${API}/users/${userId}`);
      setUserProfile(userResponse.data);

      // Fetch community stats
      const statsResponse = await axios.get(`${API}/community/stats`);
      setCommunityStats(statsResponse.data);

      // Fetch interest matches
      const matchesResponse = await axios.get(`${API}/community/matches/${userId}`);
      setInterestMatches(matchesResponse.data);

    } catch (error) {
      console.error('Error fetching community data:', error);
      if (error.response?.status === 404) {
        alert('User not found. Redirecting to registration...');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async () => {
    try {
      const profileUrl = `${window.location.origin}/profile/${userId}`;
      const qrDataUrl = await QRCode.toDataURL(profileUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleJoinDiscord = () => {
    alert('Discord integration coming soon! Join link will be provided by Algosoc team.');
  };

  const handleSaveToGoogleWallet = async () => {
    try {
      setWalletLoading(true);

      const response = await axios.post(`${API}/wallet/google/create-pass?user_id=${userId}`);

      if (response.data.success && response.data.save_url) {
        // Open Google Wallet save URL in new tab
        window.open(response.data.save_url, '_blank');
      } else {
        throw new Error('Failed to create wallet pass');
      }

    } catch (error) {
      console.error('Error creating Google Wallet pass:', error);
      alert('Failed to create wallet pass. Please try again later.');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleSaveToAppleWallet = () => {
    alert('🍎 Apple Wallet support coming soon! Stay tuned for updates.');
  };

  const handleShareProfile = () => {
    if (navigator.share) {
      navigator.share({
        title: `${userProfile.name} - OneTap Profile`,
        text: `Check out ${userProfile.name}'s profile on OneTap`,
        url: `${window.location.origin}/profile/${userId}`
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(`${window.location.origin}/profile/${userId}`);
      alert('Profile link copied to clipboard!');
    }
  };

  const downloadQRCode = async () => {
    try {
      // Download from backend API
      const response = await axios.get(`${API}/wallet/qr/${userId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OneTap_${userProfile.name.replace(/\s+/g, '_')}_QR.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading QR code:', error);
      alert('Failed to download QR code. Please try again.');
    }
  };

  const addToCalendar = (eventName, eventDate, eventTime) => {
    try {
      // Parse the date string
      const dateStr = eventDate.replace(/,/g, '');
      const dateParts = dateStr.split(' ');
      const month = dateParts[1];
      const day = dateParts[2];
      const year = dateParts[3];

      // Convert month name to number
      const months = {
        'January': '01', 'February': '02', 'March': '03', 'April': '04',
        'May': '05', 'June': '06', 'July': '07', 'August': '08',
        'September': '09', 'October': '10', 'November': '11', 'December': '12'
      };

      const monthNum = months[month];
      const dayNum = day.padStart(2, '0');

      // Parse time
      const timeParts = eventTime.replace(/\s+/g, '').toUpperCase();
      let hour = parseInt(timeParts.split(':')[0]);
      const isPM = timeParts.includes('PM');

      if (isPM && hour !== 12) hour += 12;
      if (!isPM && hour === 12) hour = 0;

      const hourStr = hour.toString().padStart(2, '0');

      // Create calendar event
      const startDate = `${year}${monthNum}${dayNum}T${hourStr}0000`;
      const endHour = (hour + 2).toString().padStart(2, '0');
      const endDate = `${year}${monthNum}${dayNum}T${endHour}0000`;

      const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventName)}&dates=${startDate}/${endDate}&details=${encodeURIComponent('OneTap Community Event')}&location=${encodeURIComponent('TBD')}`;

      window.open(calendarUrl, '_blank');
    } catch (error) {
      console.error('Error creating calendar event:', error);
      alert('Failed to create calendar event. Please add manually.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl loading-dots">Loading community data...</div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">User not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto space-y-8">

        {/* Welcome Header */}
        <div className="text-center space-y-4 fade-in" data-testid="welcome-header">
          <h1 className="text-4xl md:text-5xl font-bold gradient-text">
            Hi {userProfile.name.split(' ')[0]} 👋 You're in!
          </h1>
          <p className="text-white/80 text-lg">Welcome to the OneTap community</p>
        </div>

        {/* Enhanced User Profile Card */}
        <Card className="profile-card slide-up" data-testid="user-profile-card">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <UserCheckIcon className="w-5 h-5" />
                <span>Your OneTap Profile</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowQRCode(!showQRCode)}
                  className="btn-accent"
                  data-testid="toggle-qr-btn"
                >
                  <QrCodeIcon className="w-4 h-4 mr-1" />
                  QR
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleShareProfile}
                  className="btn-accent"
                  data-testid="share-profile-btn"
                >
                  <ShareIcon className="w-4 h-4 mr-1" />
                  Share
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">

              {/* Profile Info */}
              <div className="flex items-center space-x-4">
                <Avatar className="w-20 h-20 bg-gradient-to-br from-white to-gray-300">
                  <AvatarFallback className="text-black font-bold text-xl">
                    {getInitials(userProfile.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-xl">{userProfile.name}</h3>
                  <p className="text-white/70 text-lg">{userProfile.year_of_study}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {userProfile.interests.map((interest, index) => (
                      <Badge key={index} variant="outline" className="interest-chip selected">
                        {interest}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleSaveToGoogleWallet}
                      className="btn-secondary"
                      disabled={walletLoading}
                      data-testid="save-to-google-wallet-btn"
                    >
                      <WalletIcon className="w-4 h-4 mr-2" />
                      {walletLoading ? 'Creating...' : 'Save to Google Wallet'}
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={handleSaveToAppleWallet}
                      className="btn-secondary"
                      data-testid="save-to-apple-wallet-btn"
                    >
                      <WalletIcon className="w-4 h-4 mr-2" />
                      Apple Wallet (Coming Soon)
                    </Button>
                  </div>
                </div>
              </div>

              {/* QR Code Section */}
              {showQRCode && (
                <div className="flex flex-col items-center space-y-4">
                  <div className="qr-code-container">
                    <img 
                      src={qrCodeUrl} 
                      alt="Profile QR Code" 
                      className="w-32 h-32"
                      data-testid="profile-qr-code"
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p className="text-white/80 text-sm">Scan to view profile</p>
                    <Button 
                      size="sm" 
                      onClick={downloadQRCode}
                      className="btn-secondary"
                      data-testid="download-qr-btn"
                    >
                      <DownloadIcon className="w-4 h-4 mr-2" />
                      Download QR
                    </Button>
                  </div>
                </div>
              )}

            </div>
          </CardContent>
        </Card>

        {/* Community Stats */}
        {communityStats && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* Total Members */}
            <Card className="glass border-white/20 slide-up" data-testid="total-members-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-white to-gray-300 rounded-lg flex items-center justify-center">
                    <UsersIcon className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Total Members</p>
                    <p className="text-white font-bold text-2xl">{communityStats.total_members}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Interest Matches */}
            {interestMatches && (
              <Card className="glass border-white/20 slide-up" data-testid="interest-matches-card">
                <CardContent className="p-6">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-gray-300 to-gray-500 rounded-lg flex items-center justify-center">
                      <SparklesIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white/70 text-sm">Shared Interests</p>
                      <p className="text-white font-bold text-2xl">
                        {Object.keys(interestMatches.matches).length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Popular Interest */}
            <Card className="glass border-white/20 slide-up" data-testid="popular-interest-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-lg flex items-center justify-center">
                    <TrendingUpIcon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Top Interest</p>
                    <p className="text-white font-bold text-lg">
                      {communityStats.shared_interests && 
                       Object.keys(communityStats.shared_interests).length > 0 ?
                       Object.entries(communityStats.shared_interests).sort((a, b) => b[1] - a[1])[0][0] :
                       'Python'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Smart Highlights */}
        {interestMatches && Object.keys(interestMatches.matches).length > 0 && (
          <Card className="glass border-white/20 slide-up" data-testid="smart-highlights">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <SparklesIcon className="w-5 h-5" />
                <span>Smart Connections</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(interestMatches.match_summary).map(([interest, count]) => (
                <div key={interest} className="flex items-center justify-between">
                  <span className="text-white/90">
                    You share <span className="font-semibold text-white">'{interest}'</span> with others
                  </span>
                  <Badge className="bg-white/20 text-white border-white/30">
                    {count} {count === 1 ? 'person' : 'people'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Live Community Board */}
        {communityStats && communityStats.recent_members && (
          <Card className="glass border-white/20 slide-up" data-testid="community-board">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-400 rounded-full pulse"></div>
                <span>Live Community Board</span>
              </CardTitle>
              <p className="text-white/70 text-sm">Recent members who joined OneTap</p>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {communityStats.recent_members.slice(0, 6).map((member, index) => (
                  <div key={member.id || index} className="member-card" data-testid={`member-card-${index}`}>
                    <div className="flex items-center space-x-3">
                      <Avatar className="w-10 h-10 bg-gradient-to-br from-white to-gray-300">
                        <AvatarFallback className="text-black font-medium text-sm">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{member.name}</p>
                        <p className="text-white/60 text-sm">{member.year_of_study}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-3">
                      {member.interests.slice(0, 2).map((interest, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs interest-chip">
                          {interest}
                        </Badge>
                      ))}
                      {member.interests.length > 2 && (
                        <Badge variant="outline" className="text-xs interest-chip">
                          +{member.interests.length - 2}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* OneTap x Algosoc Spotlight */}
        <Card className="glass border-white/20 slide-up" data-testid="algosoc-spotlight">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <TrendingUpIcon className="w-5 h-5" />
              <span>OneTap × Algosoc Spotlight</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">

            {/* Upcoming Events */}
            <div className="space-y-3">
              <h4 className="text-white font-semibold flex items-center space-x-2">
                <CalendarIcon className="w-4 h-4" />
                <span>Upcoming Events</span>
              </h4>
              <div className="space-y-2">
                {upcomingEvents.map((event, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                    <div className="flex-1">
                      <p className="text-white font-medium">{event.name}</p>
                      <p className="text-white/70 text-sm">{event.date} • {event.time}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {event.isSoon && (
                        <Badge className="bg-green-400/20 text-green-300 border-green-400/30">
                          This Week
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-white/70 hover:text-white"
                        onClick={() => addToCalendar(event.name, event.date, event.time)}
                      >
                        <ClockIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-white/20" />

            {/* Committee Intro */}
            <div className="space-y-3">
              <h4 className="text-white font-semibold">Meet the Committee</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {committeeMembers.map((member, index) => (
                  <div key={index} className="text-center space-y-2">
                    <Avatar className="w-12 h-12 mx-auto bg-gradient-to-br from-white to-gray-300">
                      <AvatarFallback className="text-black font-medium text-sm">
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-white text-sm font-medium">{member.name}</p>
                      <p className="text-white/60 text-xs">{member.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-white/20" />

            {/* Join Discord */}
            <div className="text-center space-y-4">
              <div className="space-y-2">
                <h4 className="text-white font-semibold">Ready to Connect?</h4>
                <p className="text-white/70 text-sm">Join our Discord server to chat with fellow members</p>
              </div>
              <Button 
                onClick={handleJoinDiscord}
                className="btn-primary"
                data-testid="join-discord-btn"
              >
                <ExternalLinkIcon className="w-4 h-4 mr-2" />
                Join the Discord
              </Button>
            </div>

          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center space-y-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="btn-secondary"
            data-testid="back-to-landing-btn"
          >
            Back to Landing
          </Button>
          <p className="text-white/60 text-sm">
            Welcome to OneTap • Instant connections, lasting networks 🚀
          </p>
        </div>

      </div>
    </div>
  );
};

export default CommunityHub;
